import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Camera, ShieldCheck, ShieldX,
  Smile, ScanFace, Sparkles, RotateCcw, CheckCircle2,
} from 'lucide-react';

// Liveness challenge prompts — 1 randomly picked so users can't pre-record.
const CHALLENGES = [
  { id: 'smile',           icon: Smile,    label: 'Big smile — show your teeth' },
  { id: 'turn_head_left',  icon: RotateCcw, label: 'Turn your head left' },
  { id: 'turn_head_right', icon: RotateCcw, label: 'Turn your head right' },
  { id: 'open_mouth',      icon: Smile,    label: "Open your mouth (say 'ahh')" },
];

/**
 * Two-step verification:
 *   Step 1 (identity): 1-selfie face match against profile photo (existing flow).
 *                      Server also runs age estimation — if flagged possibly-minor,
 *                      we soft-fail and admin gets alerted.
 *   Step 2 (liveness): 2-selfie challenge — neutral photo + a randomly-chosen
 *                      expression/pose challenge. Blocks photo-of-a-photo attacks.
 *
 * Users can complete step 1 without step 2; that grants only the base
 * `selfie_verified` badge. Completing step 2 grants the higher-trust
 * `liveness_verified` badge (visible with a green ring on the shield).
 */
export default function Verification() {
  const navigate = useNavigate();
  const [step, setStep] = useState('identity');   // 'identity' | 'liveness-intro' | 'liveness-neutral' | 'liveness-challenge' | 'liveness-done'
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);      // identity outcome
  const [neutralBlob, setNeutralBlob] = useState(null);
  const [neutralPreview, setNeutralPreview] = useState(null);
  const [challenge, setChallenge] = useState(CHALLENGES[0]);
  const [livenessResult, setLivenessResult] = useState(null);

  const identityFileRef = useRef(null);
  const neutralFileRef = useRef(null);
  const challengeFileRef = useRef(null);

  // Pick a random challenge on mount so users can't pre-guess
  useEffect(() => {
    setChallenge(CHALLENGES[Math.floor(Math.random() * CHALLENGES.length)]);
  }, []);

  // === STEP 1 — identity selfie ===
  const handleIdentity = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Select an image'); return; }
    setUploading(true); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post('/verify/selfie', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(r.data);
      if (r.data.verified) {
        toast.success('Identity verified — one more step for the higher-trust badge');
        setStep('liveness-intro');
      } else {
        toast.error(r.data.reason || 'Verification failed. Try again with a clearer photo.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Verification failed');
    } finally { setUploading(false); }
  };

  // === STEP 2 — neutral selfie for liveness challenge ===
  const handleNeutral = (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Select an image'); return; }
    setNeutralBlob(file);
    setNeutralPreview(URL.createObjectURL(file));
    setStep('liveness-challenge');
  };

  // === STEP 3 — challenge selfie + submit ===
  const handleChallenge = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) { toast.error('Select an image'); return; }
    if (!neutralBlob) { toast.error('Take the neutral photo first'); return; }
    setUploading(true); setLivenessResult(null);
    try {
      const fd = new FormData();
      fd.append('neutral', neutralBlob);
      fd.append('challenge', file);
      fd.append('challenge_type', challenge.id);
      const r = await api.post('/verify/liveness', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setLivenessResult(r.data);
      if (r.data.passed) {
        toast.success('Liveness verified! Higher-trust badge added.');
        setStep('liveness-done');
      } else {
        toast.error(r.data.reason || 'Liveness check failed. Try again.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Liveness check failed');
    } finally { setUploading(false); }
  };

  const retryLiveness = () => {
    setNeutralBlob(null);
    setNeutralPreview(null);
    setLivenessResult(null);
    setChallenge(CHALLENGES[Math.floor(Math.random() * CHALLENGES.length)]);
    setStep('liveness-neutral');
  };

  const ChallengeIcon = challenge.icon;

  return (
    <div className="min-h-screen bg-white pb-[80px]" data-testid="verification-page">
      {/* Header */}
      <div className="px-5 pt-6 pb-3 flex items-center gap-3">
        <button
          onClick={() => step === 'identity' ? navigate(-1) : setStep('identity')}
          className="w-9 h-9 rounded-full hover:bg-black/[0.04] flex items-center justify-center text-[#4B4B52]"
          data-testid="verify-back">
          <ArrowLeft size={18}/>
        </button>
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93]">VERIFICATION</p>
          <h1 className="font-display text-[22px] font-[600] text-[#0F0F10] leading-tight">
            {step.startsWith('liveness') ? 'Liveness Check' : 'Verify Profile'}
          </h1>
        </div>
      </div>

      {/* Progress dots */}
      <div className="px-5 mb-6 flex items-center gap-2">
        <span className={`h-1 flex-1 rounded-full ${result?.verified ? 'bg-[#15803D]' : step === 'identity' ? 'bg-[#B91C1C]' : 'bg-black/[0.08]'}`}/>
        <span className={`h-1 flex-1 rounded-full ${
          step === 'liveness-done' ? 'bg-[#15803D]' :
          step.startsWith('liveness') ? 'bg-[#B91C1C]' : 'bg-black/[0.08]'
        }`}/>
      </div>

      <div className="px-5 space-y-6">
        {/* ===== STEP 1: IDENTITY ===== */}
        {step === 'identity' && (
          <>
            <div className="bg-[#F8F9FA] border border-black/[0.06] rounded-2xl p-6 text-center" data-testid="step-identity">
              <div className="w-20 h-20 rounded-full bg-[rgba(30,79,216,0.08)] flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-[#1E4FD8]" />
              </div>
              <h2 className="text-lg font-bold mb-2">Step 1 — Take a Selfie</h2>
              <p className="text-zinc-500 text-sm mb-6 max-w-xs mx-auto">
                Take a clear selfie to verify you&apos;re the person in your profile photos. Verified profiles get 3× more matches.
              </p>
              <input type="file" ref={identityFileRef} accept="image/*" capture="user" onChange={handleIdentity} className="hidden" />
              <button
                onClick={() => identityFileRef.current?.click()}
                disabled={uploading}
                data-testid="take-selfie-btn"
                className="bg-[#1E4FD8] hover:bg-[#1940AE] text-white px-8 py-3.5 rounded-xl font-semibold transition-all hover:scale-[1.02] shadow-[0_4px_14px_-2px_rgba(30,79,216,0.45)] disabled:opacity-50">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Verifying...</> : <><Camera className="w-4 h-4 inline mr-2" /> Take Selfie</>}
              </button>
            </div>

            {result && !result.verified && (
              <div className="rounded-2xl p-6 border bg-red-500/5 border-red-500/20" data-testid="verification-result">
                <div className="flex items-center gap-3 mb-3">
                  <ShieldX className="w-8 h-8 text-red-500" />
                  <div>
                    <h3 className="font-bold">Not Verified</h3>
                    <p className="text-sm text-zinc-500">Try again with a clearer photo</p>
                  </div>
                </div>
                <p className="text-sm text-zinc-600">{result.reason}</p>
              </div>
            )}

            <div className="bg-[#F8F9FA] border border-black/[0.06] rounded-2xl p-5">
              <p className="text-[10px] font-semibold text-zinc-500 uppercase tracking-wider mb-3">Tips</p>
              <ul className="space-y-2 text-sm text-zinc-500">
                <li>Make sure your face is clearly visible</li>
                <li>Good lighting, no sunglasses or heavy filters</li>
                <li>Match the look of your profile photos</li>
                <li>Use the front-facing camera for best results</li>
              </ul>
            </div>
          </>
        )}

        {/* ===== STEP 2 INTRO: LIVENESS ===== */}
        {step === 'liveness-intro' && (
          <div className="bg-gradient-to-br from-[#F0FDF4] to-white border border-[#15803D]/20 rounded-2xl p-6" data-testid="step-liveness-intro">
            <div className="flex items-center gap-3 mb-3">
              <ShieldCheck className="w-8 h-8 text-[#15803D]" />
              <div>
                <h3 className="font-bold">Identity verified ✓</h3>
                <p className="text-sm text-zinc-500">Confidence: {result?.confidence}%</p>
              </div>
            </div>
            <div className="rounded-xl bg-white border border-black/[0.06] p-4 my-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#B91C1C]/[0.08] flex items-center justify-center flex-shrink-0">
                  <ScanFace className="w-5 h-5 text-[#B91C1C]" />
                </div>
                <div>
                  <p className="font-semibold text-[13px] text-[#0F0F10]">Optional — Liveness challenge</p>
                  <p className="text-[11px] text-[#4B4B52] mt-1 leading-relaxed">
                    Prove you&apos;re a real, live person (not a photo). Takes 20 seconds.
                    You&apos;ll take two quick selfies: one neutral, then one with a random expression.
                    Passing earns the higher-trust <span className="font-bold">Live Verified</span> badge.
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setStep('liveness-neutral')}
                data-testid="liveness-start-btn"
                className="flex-1 bg-[#0F0F10] hover:bg-[#0F0F10]/90 text-white py-3 rounded-xl font-semibold text-sm transition inline-flex items-center justify-center gap-1.5">
                <Sparkles size={14}/> Start liveness check
              </button>
              <button
                onClick={() => navigate('/profile')}
                data-testid="liveness-skip-btn"
                className="px-4 py-3 rounded-xl border border-black/[0.08] text-[13px] font-semibold text-[#4B4B52] hover:bg-black/[0.03] transition">
                Skip
              </button>
            </div>
          </div>
        )}

        {/* ===== STEP 3: NEUTRAL SELFIE ===== */}
        {step === 'liveness-neutral' && (
          <div className="bg-[#F8F9FA] border border-black/[0.06] rounded-2xl p-6 text-center" data-testid="step-liveness-neutral">
            <div className="w-20 h-20 rounded-full bg-black/[0.05] flex items-center justify-center mx-auto mb-4">
              <ScanFace className="w-8 h-8 text-[#4B4B52]" />
            </div>
            <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#8A8A93] mb-1">STEP 1 OF 2</p>
            <h2 className="text-lg font-bold mb-2">Take a neutral selfie</h2>
            <p className="text-zinc-500 text-sm mb-6 max-w-xs mx-auto">
              Look straight at the camera with a neutral expression. Don&apos;t smile yet — we&apos;ll ask you to next.
            </p>
            <input type="file" ref={neutralFileRef} accept="image/*" capture="user" onChange={handleNeutral} className="hidden" />
            <button
              onClick={() => neutralFileRef.current?.click()}
              data-testid="take-neutral-btn"
              className="bg-[#0F0F10] hover:bg-black text-white px-8 py-3.5 rounded-xl font-semibold transition-all hover:scale-[1.02] inline-flex items-center gap-2">
              <Camera size={16}/> Take neutral selfie
            </button>
          </div>
        )}

        {/* ===== STEP 4: CHALLENGE ===== */}
        {step === 'liveness-challenge' && (
          <>
            {neutralPreview && (
              <div className="rounded-2xl overflow-hidden border border-black/[0.06] w-24 h-24 relative">
                <img src={neutralPreview} alt="" className="w-full h-full object-cover"/>
                <span className="absolute bottom-1 left-1 text-[9px] font-bold bg-black/60 text-white px-1.5 py-0.5 rounded-full">
                  NEUTRAL
                </span>
              </div>
            )}
            <div className="bg-gradient-to-br from-[#FEF2F2] to-white border border-[#B91C1C]/20 rounded-2xl p-6 text-center" data-testid="step-liveness-challenge">
              <div className="w-20 h-20 rounded-full bg-[#B91C1C]/[0.10] flex items-center justify-center mx-auto mb-4">
                <ChallengeIcon className="w-9 h-9 text-[#B91C1C]" strokeWidth={1.8}/>
              </div>
              <p className="text-[10px] font-bold uppercase tracking-[1.4px] text-[#B91C1C] mb-1">STEP 2 OF 2 · CHALLENGE</p>
              <h2 className="font-display text-[20px] font-[600] mb-2 leading-tight">{challenge.label}</h2>
              <p className="text-zinc-500 text-sm mb-6 max-w-xs mx-auto">
                Now take a second selfie, this time {challenge.label.toLowerCase()}.
                This proves you&apos;re a live person, not a photo.
              </p>
              <input type="file" ref={challengeFileRef} accept="image/*" capture="user" onChange={handleChallenge} className="hidden" />
              <button
                onClick={() => challengeFileRef.current?.click()}
                disabled={uploading}
                data-testid="take-challenge-btn"
                className="bg-[#B91C1C] hover:bg-[#7F1D1D] text-white px-8 py-3.5 rounded-xl font-semibold transition-all hover:scale-[1.02] shadow-[0_4px_14px_-2px_rgba(185,28,28,0.4)] disabled:opacity-50 inline-flex items-center gap-2">
                {uploading ? <><Loader2 className="w-4 h-4 animate-spin"/> Analyzing…</> : <><Camera size={16}/> Take challenge selfie</>}
              </button>
            </div>

            {livenessResult && !livenessResult.passed && (
              <div className="rounded-2xl p-4 border bg-red-500/5 border-red-500/20" data-testid="liveness-result-fail">
                <div className="flex items-center gap-3 mb-2">
                  <ShieldX className="w-6 h-6 text-red-500"/>
                  <div>
                    <p className="font-bold text-[14px]">Didn&apos;t pass</p>
                    <p className="text-[11px] text-zinc-500">{livenessResult.reason}</p>
                  </div>
                </div>
                <button
                  onClick={retryLiveness}
                  data-testid="liveness-retry-btn"
                  className="w-full mt-2 bg-black/[0.05] hover:bg-black/[0.08] text-[#0F0F10] py-2 rounded-xl font-semibold text-[12px] inline-flex items-center justify-center gap-1.5">
                  <RotateCcw size={12}/> Try again
                </button>
              </div>
            )}
          </>
        )}

        {/* ===== STEP 5: DONE ===== */}
        {step === 'liveness-done' && (
          <div className="bg-gradient-to-br from-[#F0FDF4] to-white border border-[#15803D]/20 rounded-2xl p-6 text-center" data-testid="step-liveness-done">
            <div className="w-20 h-20 rounded-full bg-[#15803D]/[0.12] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-10 h-10 text-[#15803D]" strokeWidth={1.8}/>
            </div>
            <h2 className="font-display text-[22px] font-[600] mb-2">Live Verified ✨</h2>
            <p className="text-zinc-500 text-sm mb-6 max-w-xs mx-auto">
              Confidence: {livenessResult?.confidence}%. Higher-trust badge added to your profile — matches will see the green ring.
            </p>
            <button
              onClick={() => navigate('/profile')}
              data-testid="liveness-done-back"
              className="bg-[#0F0F10] hover:bg-black text-white px-8 py-3.5 rounded-xl font-semibold transition-all hover:scale-[1.02]">
              Back to profile
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
